import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  Home as HomeIcon,
  FileText,
  Calendar,
  CreditCard,
  MessageSquare,
  Users,
  Shield,
  Bell,
  LogOut,
  Lock,
  Menu,
  X,
  Globe,
  MessagesSquare,
  CalendarDays,
  History,
  AlertTriangle
} from 'lucide-react';
import { useState } from 'react';

const DashboardLayout = ({ children, role }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const getMenuItems = () => {
    switch (role) {
      case 'resident':
        return [
          { icon: HomeIcon, label: 'Dashboard', path: '/resident' },
          { icon: FileText, label: 'Complaints', path: '/resident/complaints' },
          { icon: Calendar, label: 'Amenities', path: '/resident/amenities' },
          { icon: CreditCard, label: 'Bills', path: '/resident/bills' },
          { icon: Users, label: 'Visitors', path: '/resident/visitors' },
          { icon: Users, label: 'My Tenants', path: '/resident/tenants' },
          { icon: MessageSquare, label: 'AI Assistant', path: '/resident/chat' },
          { icon: Bell, label: 'Announcements', path: '/resident/announcements' },
          { icon: MessagesSquare, label: 'Community Forum', path: '/resident/forum' },
          { icon: CalendarDays, label: 'Events', path: '/resident/events' },
        ];
      case 'tenant':
        return [
          { icon: HomeIcon, label: 'Dashboard', path: '/tenant' },
          { icon: CreditCard, label: 'Rent Payments', path: '/tenant/rent' },
          { icon: Calendar, label: 'Amenities', path: '/tenant/amenities' },
          { icon: FileText, label: 'Bills', path: '/tenant/bills' },
          { icon: Users, label: 'Visitors', path: '/tenant/visitors' },
          { icon: FileText, label: 'Complaints', path: '/tenant/complaints' },
          { icon: MessagesSquare, label: 'Community Forum', path: '/tenant/forum' },
          { icon: Bell, label: 'Announcements', path: '/tenant/announcements' },
          { icon: CalendarDays, label: 'Events', path: '/tenant/events' },
          { icon: MessageSquare, label: 'AI Assistant', path: '/tenant/chat' },
        ];
      case 'security':
        return [
          { icon: HomeIcon, label: 'Dashboard', path: '/security' },
          { icon: Users, label: 'Visitor Management', path: '/security/visitors' },
          { icon: Calendar, label: 'Amenity Check-In', path: '/security/amenities' },
          { icon: Bell, label: 'Announcements', path: '/security/announcements' },
          { icon: CalendarDays, label: 'Events', path: '/security/events' },
        ];
      case 'admin':
        return [
          { icon: HomeIcon, label: 'Dashboard', path: '/admin' },
          { icon: Users, label: 'Personnel', path: '/admin/residents' },
          { icon: Users, label: 'Occupancy & Tenants', path: '/admin/occupancy' },
          { icon: Calendar, label: 'Amenities', path: '/admin/amenities' },
          { icon: CreditCard, label: 'Bills', path: '/admin/bills' },
          { icon: Bell, label: 'Announcements', path: '/admin/announcements' },
          { icon: FileText, label: 'Complaints', path: '/admin/complaints' },
          { icon: History, label: 'Activity Log', path: '/admin/activity-log' },
          { icon: Users, label: 'Removal & Rejection', path: '/admin/removal-history' },
          { icon: MessagesSquare, label: 'Community Forum', path: '/admin/forum' },
          { icon: CalendarDays, label: 'Events', path: '/admin/events' },
        ];
      default:
        return [];
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/home');
  };

  const menuItems = getMenuItems();

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="flex">
        {/* Sidebar */}
        <aside
          className={cn(
            "fixed left-0 top-0 h-screen bg-white border-r border-slate-200 transition-all duration-300 z-40",
            sidebarOpen ? "w-64" : "w-20"
          )}
        >
          <div className="flex flex-col h-full">
            {/* Logo - Clickable to go Home */}
            <div className="p-6 border-b border-slate-200">
              <button
                onClick={() => navigate('/home')}
                className="flex items-center gap-3 hover:opacity-80 transition-opacity cursor-pointer"
                data-testid="dashboard-logo-home-btn"
                title="Go to Home Page"
              >
                <div className="h-10 w-10 bg-secondary rounded-xl flex items-center justify-center flex-shrink-0">
                  <Lock className="h-6 w-6 text-white" />
                </div>
                {sidebarOpen && (
                  <span className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                    FortiHome
                  </span>
                )}
              </button>
            </div>

            {/* Menu Items */}
            <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
              {/* Home Page Link */}
              <button
                onClick={() => navigate('/home')}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all text-slate-600 hover:bg-slate-100"
                data-testid="nav-home-page"
                title="Go to Home Page"
              >
                <Globe className="h-5 w-5 flex-shrink-0" />
                {sidebarOpen && <span className="font-medium">Home Page</span>}
              </button>
              
              <div className="h-px bg-slate-200 my-2"></div>
              
              {menuItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <button
                    key={item.path}
                    onClick={() => navigate(item.path)}
                    className={cn(
                      "w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all",
                      isActive
                        ? "bg-secondary text-white"
                        : "text-slate-600 hover:bg-slate-100"
                    )}
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <Icon className="h-5 w-5 flex-shrink-0" />
                    {sidebarOpen && <span className="font-medium">{item.label}</span>}
                  </button>
                );
              })}
            </nav>

            {/* User Section */}
            <div className="p-4 border-t border-slate-200">
              {sidebarOpen && user && (
                <div className="mb-3 px-4 py-3 bg-slate-50 rounded-xl">
                  <p className="text-sm font-semibold text-slate-900">{user.name}</p>
                  <p className="text-xs text-slate-500 capitalize">{user.role}</p>
                </div>
              )}
              <Button
                variant="outline"
                className="w-full justify-start gap-3"
                onClick={handleLogout}
                data-testid="logout-btn"
              >
                <LogOut className="h-5 w-5" />
                {sidebarOpen && <span>Logout</span>}
              </Button>
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <div className={cn("flex-1 transition-all duration-300", sidebarOpen ? "ml-64" : "ml-20")}>
          {/* Header */}
          <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-xl border-b border-slate-200">
            <div className="px-4 md:px-6 py-4 flex items-center justify-between">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSidebarOpen(!sidebarOpen)}
                data-testid="sidebar-toggle-btn"
              >
                {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </Button>
              <div className="flex items-center gap-4">
                <span className="text-xs md:text-sm text-slate-600">
                  Welcome back, <span className="font-semibold">{user?.name}</span>
                </span>
              </div>
            </div>
          </header>

          {/* Page Content */}
          <main className="p-4 md:p-6">
            {user?.status === 'pending_vacancy' && (
              <div className="mb-4 p-4 bg-orange-50 border border-orange-300 rounded-xl flex items-start gap-3" data-testid="global-vacancy-banner">
                <AlertTriangle className="h-5 w-5 text-orange-600 mt-0.5 shrink-0" />
                <div>
                  <p className="font-semibold text-orange-800">Vacancy Notice in Progress</p>
                  <p className="text-sm text-orange-700">Your landlord has submitted a vacancy notification. This is being reviewed by the admin. You can continue using the platform until a final decision is made.</p>
                </div>
              </div>
            )}
            {user?.status === 'pending_removal' && (
              <div className="mb-4 p-4 bg-red-50 border border-red-300 rounded-xl flex items-start gap-3" data-testid="global-removal-banner">
                <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 shrink-0" />
                <div>
                  <p className="font-semibold text-red-800">Removal Process in Progress</p>
                  <p className="text-sm text-red-700">A removal request has been submitted for your account. This is pending admin review. Please contact your landlord or admin for more details.</p>
                </div>
              </div>
            )}
            {children}
          </main>
        </div>
      </div>
    </div>
  );
};

export default DashboardLayout;